import { useState, useMemo } from "react";
import ActionTitleFilter from "./ActionTitleFilter";
import MapperView from "./MapperView";
import PublicView from "./PublicView";

type VenueMapperNotesProps = {
  venueDetailsReponse: any;
  onRefresh: () => void;
  showMapperView: boolean;
  onEdit: (note: any) => void;
  onFlagNote: (note: any) => void;
};

const VenueMapperNotes = ({
  venueDetailsReponse,
  onRefresh,
  showMapperView,
  onEdit,
  onFlagNote,
}: VenueMapperNotesProps) => {
  // Get notes data
  const mapperNotes = venueDetailsReponse.details.businessNotesForMappers || [];
  const publicNotes = useMemo(() => venueDetailsReponse.details.businessSociabilitySaysApproved || [], [venueDetailsReponse.details.businessSociabilitySaysApproved]);

  // Determine default tab based on available notes
  const defaultNoteType = useMemo(() => {
    const hasMapperNotes = mapperNotes.length > 0;
    const hasPublicNotes = publicNotes.length > 0;
    
    // If there are mapper notes, select Mapper tab
    if (hasMapperNotes) {
      return "Mapper";
    }
    // If there are no mapper notes but there are public notes, select Public tab
    else if (hasPublicNotes) {
      return "Public";
    }
    // If there are neither, default to Mapper tab
    else {
      return "Mapper";
    }
  }, [mapperNotes.length, publicNotes.length]);

  const [selectedNoteType, setSelectedNoteType] = useState<"Mapper" | "Public">(defaultNoteType);
  const [publicNotesSortOrder, setPublicNotesSortOrder] = useState(1);
  
  // Get note actions using useMemo to prevent unnecessary rerenders
  const noteActions = useMemo(() => {
    return venueDetailsReponse.details.businessNotesActionsVenueNotes || [];
  }, [venueDetailsReponse.details.businessNotesActionsVenueNotes]);
  
  // Determine default action title based on available notes
  const defaultActionTitle = useMemo(() => {
    if (!noteActions || noteActions.length === 0) return undefined;
    
    // Create a map of action IDs to count of notes for that action
    const actionNotesCountMap = new Map();
    
    // Initialize all actions with 0 notes
    noteActions.forEach(action => {
      actionNotesCountMap.set(action.id, 0);
    });
    
    // Count notes for each action using business_notes_action_id
    publicNotes.forEach(note => {
      // Get the action ID from the note
      const actionId = note.business_notes_action_id;
      
      if (actionId && actionNotesCountMap.has(actionId)) {
        actionNotesCountMap.set(actionId, actionNotesCountMap.get(actionId) + 1);
      }
    });
    
    // Find the first action (by ID order) that has notes
    const actionsWithNotes = noteActions
      .filter(action => actionNotesCountMap.get(action.id) > 0)
      .sort((a, b) => a.id - b.id);
    
    // If there's at least one action with notes, select it
    if (actionsWithNotes.length > 0) {
      return actionsWithNotes[0].title;
    }
    
    // If no actions have notes, just select the first action by ID
    return noteActions.sort((a, b) => a.id - b.id)[0].title;
  }, [noteActions, publicNotes]);
  
  const [selectedActionTitle, setSelectedActionTitle] = useState<string>(defaultActionTitle);

  const filteredPublicNotes = useMemo(() => {
    if (!selectedActionTitle) return publicNotes;
    return publicNotes.filter(note => {
      // Check if note has the expected structure
      if (!note || typeof note !== 'object') return false;
      
      // Try different possible paths to action_title
      const actionTitle = note.action_title || 
                         (note.note && note.note.action_title) || 
                         (note.details && note.details.action_title);
      
      return actionTitle === selectedActionTitle;
    });
  }, [publicNotes, selectedActionTitle]);

  const sortedPublicNotes = useMemo(() => {
    if (publicNotesSortOrder === 1) {
      return [...filteredPublicNotes].sort((a, b) => {
        const dateA = a.created_at || (a.note && a.note.created_at) || (a.details && a.details.created_at);
        const dateB = b.created_at || (b.note && b.note.created_at) || (b.details && b.details.created_at);
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
    }
    if (publicNotesSortOrder === 2) {
      return [...filteredPublicNotes].sort((a, b) => {
        const dateA = a.created_at || (a.note && a.note.created_at) || (a.details && a.details.created_at);
        const dateB = b.created_at || (b.note && b.note.created_at) || (b.details && b.details.created_at);
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });
    }
    if (publicNotesSortOrder === 3) {
      return [...filteredPublicNotes].sort((a, b) => {
        const aType = a?.location_type || (a.note && a.note.location_type) || (a.details && a.details.location_type) || '';
        const bType = b?.location_type || (b.note && b.note.location_type) || (b.details && b.details.location_type) || '';
        return aType.localeCompare(bType);
      });
    }
    return filteredPublicNotes;
  }, [filteredPublicNotes, publicNotesSortOrder]);

  return (
    <div className="flex flex-col mb-[100px]">
      <ActionTitleFilter
        selectedNoteType={selectedNoteType}
        setSelectedNoteType={setSelectedNoteType}
        mapperNotesCount={mapperNotes.length}
        publicNotesCount={publicNotes.length}
        showMapperView={showMapperView}
        onSortChange={setPublicNotesSortOrder}
        noteActions={noteActions}
        venueNotes={publicNotes}
        selectedActionTitle={selectedActionTitle}
        setSelectedActionTitle={setSelectedActionTitle}
      />

      {selectedNoteType === "Mapper" && (
        <MapperView mapperNotes={mapperNotes} onRefresh={onRefresh} />
      )}

      {selectedNoteType === "Public" && (
        <PublicView
          publicNotes={sortedPublicNotes}
          onRefresh={onRefresh}
          onEdit={onEdit}
          onFlagNote={onFlagNote}
          showMapperView={showMapperView}
          selectedActionTitle={selectedActionTitle}
          noteActions={noteActions}
        />
      )}
    </div>
  );
};

export default VenueMapperNotes; 